// packages/storefront/src/contexts/LocalizationContext.tsx
import React, { useState, useMemo, createContext } from 'react';
import type { CommonKey, CommonT } from '@tourtask/i18n/keys';
import { createCommonTranslatorFromMessages } from '@tourtask/i18n/createNamespacedTranslator';
import { normalizeLocale } from '@tourtask/i18n/shared/utils/normalizeLocale';
import { formatDateToLocal as sharedFormatDateToLocal } from '@tourtask/utils/shared/dates/formatDateToLocal';

// `getStr` IS the shared `TFunc`, not merely shaped like it. It used to take a
// `strCode: string`, which meant embed — the one surface a paying customer
// checks out on — was the only app where a typo'd or renamed key compiled and
// then rendered as `NO STRING IMPORTED WITH VAR NAME` in the cart.
//
// The dictionary embed holds is a SUBSET (whatever `STRINGS_TO_FETCH` asked
// for), so the type says "is a real key", not "was fetched". The second half is
// what `scripts/i18n/checks/embedFetchLists.ts` covers.

interface LocalizationContextType {
	getStr: CommonT;
	setStrings: (strings: { [key: string]: string }) => void;
	formatDateToLocal: (dateAdded: Date) => string;
	langCode: string;
	setLangCode: (langCode: string) => void;
}

const defaultContextValue: LocalizationContextType = {
	// Default implementation to avoid undefined errors. `has` answers false because nothing
	// is loaded yet, which is the truthful answer and keeps the shape a real `CommonT`.
	getStr: Object.assign(() => 'No strings have been loaded yet!', { has: () => false }),
	setStrings: () => {},
	formatDateToLocal: () => 'No date provided',
	langCode: '',
	setLangCode: () => {}
};

const LocalizationContext = createContext<LocalizationContextType>(defaultContextValue);

/** A language and its dictionary (`POST /v1/businesses/{id}/locales/{lang}/strings`), known before the first render. */
export interface LocalizationInitial {
	langCode: string;
	strings: { [key: string]: string };
}

interface LocalizationProviderProps {
	children: React.ReactNode;
	/**
	 * What to start with. A TourTask site renders the storefront on the server with both already known
	 * (docs/website-builder/design/storefront.md S4); without them the provider starts empty and the widget's
	 * boot fetch fills it through `setLangCode` / `setStrings`.
	 */
	initial?: LocalizationInitial;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children, initial }) => {
	const [strings, setStrings] = useState<{ [key: string]: string }>(() => initial?.strings ?? {});
	const [langCode, setLangCode] = useState(() => initial?.langCode ?? '');

	// Formatting goes through the SAME ICU translator the next-intl apps use (use-intl,
	// already in all three bundles), so an ICU plural/select or a number argument renders
	// here exactly as it does in the backoffice. A home-grown `{name}` substitution showed
	// the raw ICU source to customers for any such key, and nothing in tsc or
	// `i18n:check` could keep one out of a fetch list.
	//
	// `normalizeLocale` only matters before the strings arrive: the strings route refuses
	// an unsupported locale, so a non-empty dictionary always comes with a real `langCode`.
	// Memoised because the translator carries its own compiled-message cache.
	const getStr: CommonT = useMemo(() => {
		const translator = createCommonTranslatorFromMessages({ locale: normalizeLocale(langCode), messages: strings });
		return Object.assign(
			(strCode: Parameters<CommonT>[0], values?: Parameters<CommonT>[1]): string => {
				if (!translator.has(strCode)) {
					// LOUD in development, visible-but-survivable in production.
					//
					// The key is real (the catalog union proves that) but was not FETCHED — it is
					// missing from this entry's `STRINGS_TO_FETCH`. That is a one-line fix, and the
					// only reason it ever reached a customer is that the placeholder looks like
					// ordinary breakage rather than an actionable message. `pnpm i18n:check`'s
					// coverage gate now catches it before the build; this catches whatever the
					// static scan could not see (a key chosen at runtime).
					if (process.env.NODE_ENV !== 'production') {
						throw new Error(
							`Translation key "${strCode}" was rendered but never fetched. Add it to this ` +
								"entry's STRINGS_TO_FETCH list (packages/i18n/src/storefront/stringsToFetch.ts, apps/embed/src/**/…StringsToFetch.ts).",
						);
					}
					return `NO STRING IMPORTED WITH VAR NAME "${strCode}"`;
				}
				return translator(strCode, values);
			},
			{
				// The membership probe `CommonT` promises. embed holds a SUBSET of the catalog
				// (whatever `STRINGS_TO_FETCH` asked for), so this is the only honest answer to
				// "is this key available here" — and it is what lets the shared open-family
				// bridge (`translateOpenKey`, `translateOpenKeyOrNull`) work from embed too,
				// instead of embed needing its own parallel path.
				has: (key: CommonKey) => translator.has(key),
			},
		);
	}, [strings, langCode]);

	const formatDateToLocal = (dateAdded: Date) => sharedFormatDateToLocal(dateAdded, langCode);

	return (
		<LocalizationContext.Provider value={{ getStr, setStrings, formatDateToLocal, setLangCode, langCode }}>
			{children}
		</LocalizationContext.Provider>
	);
};

export default LocalizationContext;